import {CurrencyCodes} from '@/enums/CurrencyCodes';
import {Types} from "mongoose";
import {GoogleTravelModesEnum} from "@/classes/api/google/GoogleTravelModes.enum";

/**
 * CAPI-konforme Content-Items:
 * In contents[*] sind NUR diese Felder erlaubt.
 */
export interface CapiContentItem {
    id: string;
    quantity?: number;
    item_price?: number;
}

/**
 * (Optional) Erweiterte Item-Infos NUR clientenseitig/zu Analytics-Zwecken.
 * NICHT innerhalb von `custom_data.contents[*]` an Meta senden!
 * Falls benötigt, bitte separat auf Root-Ebene mitschicken (siehe unten: item_names[]).
 */
export interface UiItemInfo {
    // z.B. interner Produktname
    name?: string;
    // z.B. Anzeigezwecke
    displayName?: string;
    // frühere per-Item-Infos NICHT in contents[*] pushen.
}

/** Jedes Event kann eine event_id tragen (wird bei dir im CookieManager erzeugt). */
export type MetaEventParamsBase = {
    event_id?: string;
};

/** Standard Events von Meta */
export enum MetaTrackEvents {
    PAGE_VIEW = 'PageView',
    VIEW_CONTENT = 'ViewContent',
    INITIATE_CHECKOUT = 'InitiateCheckout',
    ADD_TO_CART = 'AddToCart',
    ADD_PAYMENT_INFO = 'AddPaymentInfo',
    PURCHASE = 'Purchase',
    LEAD = 'Lead',
    COMPLETE_REGISTRATION = 'CompleteRegistration',
    SEARCH = 'Search',
}

/** Eigene Custom Events */
export enum MetaCustomTrackEvents {
    INSTITUTION_SELECTED = 'InstitutionSelected',
    SLOTS_SELECTED = 'SlotsSelected',
    CONTACT_COMPLETED = 'ContactCompleted',
    ADDRESS_COMPLETED = 'AddressCompleted',
    CHILD_COMPLETED = 'ChildCompleted',
    CLASS_DATA_COMPLETED = 'ClassDataCompleted',
    QUESTION_ANSWERED = 'QuestionAnswered',
    TRAVEL_MODE_SELECTED = 'TravelModeSelected',
    INSTITUTION_RESPONSE_RECEIVED = 'InstitutionResponseReceived',
    INSTITUTION_RESPONSE_SELECTED = 'InstitutionResponseSelected',
    AVAILABILITIES_SELECTED = 'AvailabilitiesSelected',
    ERROR = 'Error',
    APPLICATION_STATE = 'ApplicationState',
}

/**
 * Gemeinsame Erweiterungen, die wir TOP-LEVEL in `custom_data` führen,
 * damit nichts verloren geht, aber CAPI-konform bleibt.
 *
 * - `child_count`: gesamt #Kinder (statt früher je-Item child_index)
 * - `item_names`: Liste aller betroffenen Produktnamen (statt item_name in contents[*])
 *
 * Diese Keys sind auf Root von `custom_data` unkritisch (Meta ignoriert Unbekanntes).
 */
type RootAnalyticsExtras = {
    child_count?: number;
    item_name?: string[];
};

/** Typ-Map für Standard-Events */
export type MetaEventParamsMap = {
    [MetaTrackEvents.PAGE_VIEW]: MetaEventParamsBase;

    [MetaTrackEvents.VIEW_CONTENT]: MetaEventParamsBase & {
        // Meta bietet hier keine "contents"; wenn du willst, halte Namen o.ä. in item_names
        total_price?: number;
        item_names?: string[];
    };

    [MetaTrackEvents.SEARCH]: MetaEventParamsBase & {
        search_string?: string;
        postal_code?: string;
        content_category?: string;
        previous_postal?: string;
    };

    [MetaTrackEvents.INITIATE_CHECKOUT]: MetaEventParamsBase & {
        postal_code?: string;
        previous_postal?: string;
    };

    [MetaTrackEvents.ADD_TO_CART]: MetaEventParamsBase &
        RootAnalyticsExtras & {
        // Offizielle CAPI-Felder
        content_ids?: string[];
        content_name?: string;     // Top-Level erlaubt
        content_type?: string;
        value?: number;
        currency?: CurrencyCodes;  // 'EUR'
        contents?: CapiContentItem[]; // <-- nur CAPI-Felder!

        // Deine zusätzlichen (geschäftlichen) Attribute – Top-Level in custom_data
        institution_id?: string;
        institution_name?: string;
        activity_id?: string;
        activity_name?: string;
        slot_start?: string;
        slot_end?: string;

        // Historik / Hilfsfelder
        product_id?: string[] | string;
        product_name?: string[] | string;
        previous_activity_id?: string;
        forced?: boolean;
    };

    [MetaTrackEvents.ADD_PAYMENT_INFO]: MetaEventParamsBase & {
        payment_method?: 'SEPA' | string;
        step_key?: string;
        currency?: CurrencyCodes;
    };

    [MetaTrackEvents.PURCHASE]: MetaEventParamsBase &
        RootAnalyticsExtras & {
        // Offizielle CAPI-Felder (Pflicht: value, currency, contents[])
        value: number;
        currency: CurrencyCodes;       // 'EUR'
        contents: CapiContentItem[];   // <-- nur CAPI-Felder!
        content_ids?: string[];
        num_items?: number;
        content_type?: string;
        order_id?: string;
        // Zusätzlich erlaubte (Root) Felder: item_names (s. RootAnalyticsExtras)
    };

    [MetaTrackEvents.LEAD]: MetaEventParamsBase & {
        cta?: string;
        lead_type?: 'Bewerbung';
        lead_stage?: 'success' | 'waitingList' | 'reject';
        employee_id: Types.ObjectId,
    };

    [MetaTrackEvents.COMPLETE_REGISTRATION]: MetaEventParamsBase & {
        institutionId: Types.ObjectId,
        subject?: Types.ObjectId,
    };
};

/** Typ-Map für deine Custom-Events */
export type MetaCustomEventParamsMap = {

    [MetaCustomTrackEvents.APPLICATION_STATE]: MetaEventParamsBase & {
        step_key: string;
        state: 'success' | 'waiting-list' | 'rejected';
        error?: string;
    };
    [MetaCustomTrackEvents.ERROR]: MetaEventParamsBase & {
        step_key: string;
    }
    [MetaCustomTrackEvents.AVAILABILITIES_SELECTED]: MetaEventParamsBase & {
        step_key: 'availabilities_selected';
    }
    [MetaCustomTrackEvents.INSTITUTION_RESPONSE_SELECTED]: MetaEventParamsBase & {
        institutions_in_range: Types.ObjectId[],
        institutions_amount: number,
        step_key: 'institutions_response_selected',
    };
    [MetaCustomTrackEvents.INSTITUTION_RESPONSE_RECEIVED]: MetaEventParamsBase & {
        institution_amount: number;
        institution_ids: Types.ObjectId[];
    }
    [MetaCustomTrackEvents.TRAVEL_MODE_SELECTED]: MetaEventParamsBase & {
        travel_mode: GoogleTravelModesEnum;
    };
    [MetaCustomTrackEvents.QUESTION_ANSWERED]: MetaEventParamsBase & {
        question_id: string;
        component_type: string;
    }
    [MetaCustomTrackEvents.INSTITUTION_SELECTED]: MetaEventParamsBase & {
        institution_id: string;
        institution_name: string;
        postal_code?: string;
        results_index?: number;
        page?: number;
        content_type?: 'institution';
        previous_institution_id?: string;
    };

    [MetaCustomTrackEvents.SLOTS_SELECTED]: MetaEventParamsBase & {
        offered_count: number;
        selected_count: number;
        selected_slot_ids: string[];
        previous_selection_hash?: string;
    };

    [MetaCustomTrackEvents.CONTACT_COMPLETED]: MetaEventParamsBase & {
        step_key: 'contact';
    };

    [MetaCustomTrackEvents.ADDRESS_COMPLETED]: MetaEventParamsBase & {
        step_key: 'address';
    };

    [MetaCustomTrackEvents.CHILD_COMPLETED]: MetaEventParamsBase & {
        step_key: 'child';
        child_fields_ok: boolean;
    };

    [MetaCustomTrackEvents.CLASS_DATA_COMPLETED]: MetaEventParamsBase & {
        step_key: 'classdata';
    };
};

/** Hilfstypen */
export type MetaEventName = MetaTrackEvents | MetaCustomTrackEvents;
export type MetaEventParams<K extends MetaEventName> =
    K extends MetaTrackEvents
        ? MetaEventParamsMap[K]
        : K extends MetaCustomTrackEvents
            ? MetaCustomEventParamsMap[K]
            : never;
